// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, IRawStyle, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  concatStyleSets,
  FontWeights,
  HighContrastSelector,
} from "@fluentui/react";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { ActionControlSemanticColors, AppColors } from "@1js/acui-common";
import {
  baseFabricFocusAfterSelector,
  baseFabricFocusVisibleSelector,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
  getActionControlSemanticColorsWithTheme,
  getInnerBorderStyles,
} from "@1js/acui-common";
import {
  customTokens,
  isNextStyleEnabled,
  isThemingEnabled,
} from "@1js/acui-theme";

import { getRibbonTopBarStylingConstants } from "../../ribbonStylingConstants";
import type { IPivotHeaderStyles, IPivotStyles } from "@1js/acui-pivot";
import { ConnectedTabState } from "./ConnectedTabState";
import { overflowButtonWidth } from "./TabListConstants";

const contextualTabClassName = "contextualTab";

// style used to make an inner border on a contextual tab
// switch to the browser managed highlight color for high contrast
const highlightTextBorderColorStyle = {
  selectors: {
    [HighContrastSelector]: {
      borderColor: "HighlightText",
    },
  },
};

const getNextPivotHeaderHoverPressedStyles = memoizeFunction(
  (indicatorColor: string) => {
    return {
      ["::before"]: {
        backgroundColor: indicatorColor,
        borderStyle: "solid",
        borderColor: indicatorColor,
        borderRadius: customTokens.borderRadiusCircular,
        borderWidth: `calc(${customTokens.strokeWidthThick} / 2.0)`,
        boxSizing: "border-box",
        content: '""',
        position: "absolute",
        bottom: customTokens.spacingVerticalNone,
        left: customTokens.spacingHorizontalS,
        right: customTokens.spacingHorizontalS,
        height: customTokens.strokeWidthThick,
      },
    };
  }
);

const getPivotHeaderStyles = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    pivotHeaderHeight?: number,
    useNextStyles?: boolean
  ): IButtonStyles => {
    const height =
      pivotHeaderHeight || getRibbonTopBarStylingConstants().tabHeaderHeight;
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const fontWeight = nextStylesEnabled
      ? customTokens.fontWeightRegular
      : FontWeights.semibold;
    const borderRadius = nextStylesEnabled
      ? customTokens.borderRadiusMedium
      : 0;

    return {
      root: [
        {
          backgroundColor: nextStylesEnabled
            ? customTokens.colorTransparentBackground
            : actionControlSemanticColors.tabHeaderBackgroundColor,
          color: nextStylesEnabled
            ? customTokens.colorNeutralForeground2
            : actionControlSemanticColors.textColor,
          cursor: "default",
          height,
          borderRadius,
          selectors: {
            [`${baseFabricFocusVisibleSelector} &:focus:after`]: {
              outline: "none",
              border: "none",
            },
          },
        },
        nextStylesEnabled && {
          padding: `${customTokens.spacingVerticalNone} ${customTokens.spacingHorizontalSNudge}`,
        },
      ],
      rootHovered: [
        {
          borderColor: "transparent",
          color: nextStylesEnabled
            ? customTokens.colorNeutralForeground2Hover
            : actionControlSemanticColors.textColor,
          fontWeight,
          backgroundColor: nextStylesEnabled
            ? customTokens.colorTransparentBackgroundHover
            : actionControlSemanticColors.tabHeaderHoverBackgroundColor,
        },
        nextStylesEnabled &&
          getNextPivotHeaderHoverPressedStyles(
            customTokens.colorNeutralStroke1Hover
          ),
      ],
      rootPressed: [
        {
          backgroundColor: nextStylesEnabled
            ? customTokens.colorTransparentBackgroundPressed
            : actionControlSemanticColors.tabHeaderPressedBackgroundColor,
          fontWeight,
        },
        nextStylesEnabled && {
          color: customTokens.colorNeutralForeground2Pressed,
          ...getNextPivotHeaderHoverPressedStyles(
            customTokens.colorNeutralStroke1Pressed
          ),
        },
      ],
      rootFocused: {
        fontWeight,
      },
      rootDisabled: {
        backgroundColor: nextStylesEnabled
          ? customTokens.colorTransparentBackground
          : actionControlSemanticColors.defaultBackgroundColor,
        color: nextStylesEnabled
          ? customTokens.colorNeutralForegroundDisabled
          : actionControlSemanticColors.textDisabledColor,
      },
    };
  }
);

// styles to be applied on top of pivot header styles on the selected tab header
// note: concat of style sets happens in pivotheader component
const getSelectedPivotHeaderStyleOverrides = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    useNextStyles?: boolean
  ) => {
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const fontWeight = nextStylesEnabled
      ? customTokens.fontWeightSemibold
      : FontWeights.semibold;
    return {
      root: {
        fontWeight,
        backgroundColor: nextStylesEnabled
          ? customTokens.colorTransparentBackground
          : actionControlSemanticColors.activeTabHeaderBackgroundColor,
        color: nextStylesEnabled
          ? customTokens.colorNeutralForeground1
          : actionControlSemanticColors.activeTabHeaderTextColor,
        ["&:focus:after"]: getInnerBorderStyles({
          width: 2,
          color: nextStylesEnabled
            ? customTokens.colorStrokeFocus2
            : actionControlSemanticColors.focusedBorderColor,
          gap: nextStylesEnabled
            ? { top: -1, bottom: -2, left: -1, right: -1 }
            : -1,
          radius: nextStylesEnabled
            ? customTokens.borderRadiusMedium
            : undefined,
        }),
      },
      rootHovered: {
        fontWeight,
        backgroundColor: nextStylesEnabled
          ? customTokens.colorTransparentBackgroundHover
          : actionControlSemanticColors.activeTabHeaderHoverBackgroundColor,
        color: nextStylesEnabled
          ? customTokens.colorNeutralForeground1Hover
          : actionControlSemanticColors.activeTabHeaderHoverTextColor,
      },
      rootPressed: {
        fontWeight,
        backgroundColor: nextStylesEnabled
          ? customTokens.colorTransparentBackgroundPressed
          : actionControlSemanticColors.activeTabHeaderPressedBackgroundColor,
        color: nextStylesEnabled
          ? customTokens.colorNeutralForeground1Pressed
          : actionControlSemanticColors.activeTabHeaderPressedTextColor,
      },
      rootFocused: {
        fontWeight,
        borderColor: nextStylesEnabled
          ? customTokens.colorTransparentBackground
          : actionControlSemanticColors.focusedBorderColor,
        [baseFabricFocusAfterSelector]: getInnerBorderStyles({
          width: 2,
          color: nextStylesEnabled
            ? customTokens.colorStrokeFocus2
            : actionControlSemanticColors.focusedBorderColor,
          gap: nextStylesEnabled
            ? { top: -1, bottom: -2, left: -1, right: -1 }
            : -1,
          radius: nextStylesEnabled
            ? customTokens.borderRadiusMedium
            : undefined,
        }),
      },
    };
  }
);

const getContextualTabStylesOverrides = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    appColors: AppColors,
    isSelected: boolean,
    connectedTabState: ConnectedTabState = ConnectedTabState.None,
    useNextStyles?: boolean
  ): IButtonStyles => {
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const color = nextStylesEnabled
      ? customTokens.colorBrandForeground1
      : isThemingEnabled()
        ? actionControlSemanticColors.contextualTabHeaderTextColor
        : appColors.primary;
    const fontWeight = nextStylesEnabled
      ? customTokens.fontWeightSemibold
      : FontWeights.semibold;
    return concatStyleSets({
      root: [
        contextualTabClassName,
        {
          color,
          backgroundColor: nextStylesEnabled
            ? customTokens.colorTransparentBackground
            : actionControlSemanticColors.contextualTabHeaderBackgroundColor,
          fontWeight,
          width:
            "100%" /* eliminates space between adjacent, grouped contextual tabs */,
        },
        getConnectedTabStyleOverrides(isSelected, connectedTabState),
      ],
      rootHovered: {
        color,
        fontWeight,
        backgroundColor: nextStylesEnabled
          ? customTokens.colorTransparentBackground
          : actionControlSemanticColors.contextualTabHeaderHoverBackgroundColor,
      },
      rootPressed: {
        color,
        fontWeight,
      },
    });
  }
);

function getConnectedTabStyleOverrides(
  selected: boolean,
  connectedTabState: ConnectedTabState
): IRawStyle | undefined {
  let borderOverrides: IRawStyle | undefined;
  const rect: IRawStyle = {
    left: 2,
    top: 2,
    bottom: 2,
    right: 2,
  };

  const selectedStyleOverride = selected
    ? {
        selectors: {
          "&:after": highlightTextBorderColorStyle,
        },
      }
    : undefined;

  if (connectedTabState) {
    switch (connectedTabState) {
      case ConnectedTabState.First:
        borderOverrides = {
          borderRightWidth: 0,
        };
        rect.right = 1;
        break;
      case ConnectedTabState.Last:
        borderOverrides = {
          borderLeftWidth: 0,
        };
        rect.left = 1;
        break;
      default:
        break;
    }
  }
  return {
    selectors: {
      [HighContrastSelector]: [borderOverrides, selectedStyleOverride],
      // second selector below is needed to be as specific as the one added in base button styles (styles by fabric). Then last one (this one) wins
      [`&:after, ${baseFabricFocusVisibleSelector} &:focus:after`]: [
        {
          content: '""',
          position: "absolute",
          border: `1px solid transparent`,
        },
        rect,
      ],
      // similar to above, this complex set of selectors is necessary because tab headers use base button styles from fabric and the selectors for
      // focus styles classes were long and specific. So basically one-upping those styles by adding a custom class name
      [`.${contextualTabClassName}&:hover:after, ${baseFabricFocusVisibleSelector} .${contextualTabClassName}&:focus:after`]:
        highlightTextBorderColorStyle,
    },
  };
}

const getOverflowButtonStyles = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    useNextStyles?: boolean,
    customOverflowButtonStyles?: IButtonStyles
  ): IButtonStyles => {
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const textColor = nextStylesEnabled
      ? customTokens.colorNeutralForeground2
      : isThemingEnabled()
        ? actionControlSemanticColors.textColor
        : "#444444";
    const pressedBackgroundColor = nextStylesEnabled
      ? customTokens.colorNeutralBackground3Pressed
      : isThemingEnabled()
        ? actionControlSemanticColors.tabHeaderPressedBackgroundColor
        : "#c8c8c8";
    const styles: IButtonStyles = {
      root: [
        {
          width: overflowButtonWidth,
          padding: 0,
          color: textColor,
          backgroundColor: nextStylesEnabled
            ? customTokens.colorNeutralBackground3
            : actionControlSemanticColors.tabHeaderBackgroundColor,
        },
        nextStylesEnabled && {
          borderRadius: customTokens.borderRadiusMedium,
        },
      ],
      rootPressed: {
        backgroundColor: pressedBackgroundColor,
      },
      rootExpanded: {
        backgroundColor: pressedBackgroundColor,
        color: textColor,
      },
      icon: {
        width: 12,
        height: 12,
        lineHeight: 12,
      },
      rootHovered: [
        {
          backgroundColor: nextStylesEnabled
            ? customTokens.colorNeutralBackground3Hover
            : isThemingEnabled()
              ? actionControlSemanticColors.tabHeaderHoverBackgroundColor
              : "#e1e1e1",
        },
        // High contrast styles taken from PivotHeader.styles to be consistent
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootFocused: [
        {
          backgroundColor: nextStylesEnabled
            ? customTokens.colorNeutralBackground3
            : isThemingEnabled()
              ? actionControlSemanticColors.tabHeaderBackgroundColor
              : "#e1e1e1",
          [baseFabricFocusAfterSelector]: getInnerBorderStyles({
            width: 2,
            color: nextStylesEnabled
              ? customTokens.colorStrokeFocus2
              : actionControlSemanticColors.tabHeaderFocusedBorderColor,
            gap: nextStylesEnabled
              ? { top: -1, bottom: -2, left: -1, right: -1 }
              : -1,
            radius: nextStylesEnabled
              ? customTokens.borderRadiusMedium
              : undefined,
          }),
        },
        // High contrast styles taken from PivotHeader.styles to be consistent
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
    };

    return concatStyleSets(
      getPivotHeaderStyles(actionControlSemanticColors),
      styles,
      customOverflowButtonStyles
    );
  }
);

export const getTabHeaderStyles = memoizeFunction(
  (
    theme: ITheme,
    tabHeight: number,
    styles?: Partial<IPivotHeaderStyles>,
    useNextStyles?: boolean
  ): Partial<IPivotHeaderStyles> => {
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    return {
      pivotHeader: concatStyleSets(
        getPivotHeaderStyles(
          actionControlSemanticColors,
          tabHeight,
          useNextStyles
        ),
        styles && styles.pivotHeader
      ),
      selectedPivotHeader: concatStyleSets(
        getSelectedPivotHeaderStyleOverrides(
          actionControlSemanticColors,
          useNextStyles
        ),
        styles && styles.selectedPivotHeader
      ),
      wrapper: styles && styles.wrapper,
      ghostPivotLabel: nextStylesEnabled
        ? getCustomGhostPivotLabelStyles(styles)
        : styles && styles.ghostPivotLabel,
      indicator: styles && styles.indicator,
    };
  }
);

const getCustomGhostPivotLabelStyles = (
  styles?: Partial<IPivotHeaderStyles>
) => {
  return {
    padding: "0 11px",
    ...styles?.ghostPivotLabel,
  };
};

export const getContextualTabHeaderStyles = memoizeFunction(
  (
    theme: ITheme,
    appColors: AppColors,
    tabHeight: number,
    connectedTabState?: ConnectedTabState,
    useNextStyles?: boolean
  ): Partial<IPivotHeaderStyles> => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const nextStylesEnabled = isNextStyleEnabled() && useNextStyles;
    const tabHeaderStyles = getTabHeaderStyles(
      theme,
      tabHeight,
      undefined,
      useNextStyles
    );
    const contextualTabStylesOverrides = getContextualTabStylesOverrides(
      actionControlSemanticColors,
      appColors,
      false /* isSelected */,
      connectedTabState,
      useNextStyles
    );
    const selectedContextualTabStylesOverrides =
      getContextualTabStylesOverrides(
        actionControlSemanticColors,
        appColors,
        true /* isSelected */,
        connectedTabState,
        useNextStyles
      );
    return {
      pivotHeader: concatStyleSets(
        tabHeaderStyles.pivotHeader,
        contextualTabStylesOverrides
      ),
      selectedPivotHeader: concatStyleSets(
        tabHeaderStyles.selectedPivotHeader,
        selectedContextualTabStylesOverrides
      ),
      ghostPivotLabel: nextStylesEnabled
        ? getCustomGhostPivotLabelStyles()
        : undefined,
    };
  }
);

export const getPivotStyles: (
  theme: ITheme,
  useNextStyles?: boolean,
  customStyles?: Partial<IPivotStyles>
) => IPivotStyles = memoizeFunction(
  (theme: ITheme, useNextStyles?: boolean, customStyles?: IPivotStyles) => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const rootStyles = concatStyleSets({ root: [] }, customStyles?.root);
    const overflowButtonStyles = getOverflowButtonStyles(
      actionControlSemanticColors,
      useNextStyles,
      customStyles?.overflowButton
    );
    return {
      root: rootStyles,
      overflowButton: overflowButtonStyles,
    };
  }
);
